import React, { useMemo, useState } from 'react';
import { BiNews, BiTachometer } from 'react-icons/bi';
import { AiOutlineFileSearch } from 'react-icons/ai';
import { TbReportSearch } from 'react-icons/tb';
import { TbTargetArrow } from 'react-icons/tb';
import { GoGraph } from 'react-icons/go';

import { RiSignalTowerFill, RiPulseLine, RiLightbulbFlashLine } from 'react-icons/ri';

import useViewer from 'hooks/useViewer';
import { SignalAsset } from 'utils/shared/signals/constants';
import { TICKER_FLOW_PAGE } from 'constants/pages';

import SectionTitle from 'components/SectionTitle';
import SignalAlertTable from 'components/SignalAlertTable';
import { PriceTarget, BuyingRating } from 'components/AnalystsConsensus';
import FundamentalAnalysisWidget from 'components/TradingView/FundamentalAnalysisWidget';
import TechnicalAnalysisWidget from 'components/TradingView/TechnicalAnalysisWidget';
import PulseLoader from 'components/Loading/Pulse';

import TickerNews from 'screens/TickerResearchPage/TickerNews';
import BarChartSection from 'screens/TickerResearchPage/BarChartSection';
import TickerFlowSideTable from './TickerFlowSideTable';
import TickerDarkPoolTable from 'screens/TickerResearchPage/TickerDarkPoolTable';
import useTickerFlowData from './useTickerFlowData';
import LayoutToggleButton, { formatExpandAvailableFor } from 'components/LayoutToggleButton';
import { Viewer } from 'constants/user';
import type { FlowSideBasisSelection } from 'utils/shared/trades/presets';
import { getDefaultFlowSideBasisSelection } from 'utils/shared/trades/presets';

export enum RightColumnList {
  Flow = 'FLOW',
  TickerGraphs = 'TICKER_GRAPHS',
  DarkPool = 'DARK_POOL',
  Signals = 'SIGNALS',
  TopNews = 'TOP_NEWS',
  FundamentalAnalysis = 'FUNDAMENTAL_ANALYSIS',
  TechnicalAnalysis = 'TECHNICAL_ANALYSIS',
  PriceTargetAnalysis = 'PRICE_TARGET_ANALYSIS',
}

/**
 * What each view is called. The widget buttons are icons, so this is their accessible name and
 * hover title, and it's where the disabled toggle's tooltip takes its names from: the tooltip can
 * only name views the viewer can find by hovering the buttons.
 */
export const RIGHT_COLUMN_LABELS: Readonly<Record<RightColumnList, string>> = {
  [RightColumnList.Flow]: 'Flow',
  [RightColumnList.TickerGraphs]: 'Trends',
  [RightColumnList.DarkPool]: 'Dark Pool',
  [RightColumnList.Signals]: 'Signals',
  [RightColumnList.TopNews]: 'News',
  [RightColumnList.FundamentalAnalysis]: 'Fundamental Analysis',
  [RightColumnList.TechnicalAnalysis]: 'Technical Analysis',
  [RightColumnList.PriceTargetAnalysis]: 'Analyst Consensus',
};

/**
 * The views that show more when the pane takes the full column: lists whose rows keep coming.
 * Trends, the TradingView gauges and the analyst card are fixed layouts, so a taller pane would
 * only add empty space beneath them. In menu order, which is the order the tooltip lists them.
 */
export const OPC_EXPANDABLE_WIDGETS: ReadonlySet<RightColumnList> = new Set([
  RightColumnList.Flow,
  RightColumnList.DarkPool,
  RightColumnList.Signals,
  RightColumnList.TopNews,
]);

export const isOpcExpandable = (key: RightColumnList) => OPC_EXPANDABLE_WIDGETS.has(key);

export const OPC_EXPAND_UNAVAILABLE_REASON = formatExpandAvailableFor(
  Array.from(OPC_EXPANDABLE_WIDGETS, (key) => RIGHT_COLUMN_LABELS[key]),
);

/**
 * Why the pane's expand toggle is disabled for `key`, or undefined when the view can expand. The
 * toggle stays mounted either way so the widget buttons beside it never shift, and it lines up
 * under the Positions pane's toggle above it.
 */
export const getOpcExpandDisabledReason = (key: RightColumnList): string | undefined =>
  isOpcExpandable(key) ? undefined : OPC_EXPAND_UNAVAILABLE_REASON;

const rightColumnItems = [
  {
    key: RightColumnList.Flow,
    name: (
      <span className="flex items-center">
        <RiPulseLine />
      </span>
    ),
  },
  {
    key: RightColumnList.TickerGraphs,
    name: (
      <span className="flex items-center">
        <GoGraph />
      </span>
    ),
  },
  {
    key: RightColumnList.DarkPool,
    name: (
      <span className="flex items-center">
        <RiLightbulbFlashLine />
      </span>
    ),
  },
  {
    key: RightColumnList.Signals,
    name: (
      <span className="flex items-center">
        <RiSignalTowerFill />
      </span>
    ),
  },
  {
    key: RightColumnList.TopNews,
    name: (
      <span className="flex items-center">
        <BiNews />
      </span>
    ),
  },
  {
    key: RightColumnList.FundamentalAnalysis,
    name: (
      <span className="flex items-center">
        <AiOutlineFileSearch />
      </span>
    ),
  },
  {
    key: RightColumnList.TechnicalAnalysis,
    name: (
      <span className="flex items-center">
        <BiTachometer />
      </span>
    ),
  },
  {
    key: RightColumnList.PriceTargetAnalysis,
    name: (
      <span className="flex items-center">
        <TbTargetArrow />
      </span>
    ),
  },
];

// Component that holds the flow data hook - only mounts when Flow or TickerGraphs tab is active
const FlowDataSection = React.memo(
  ({
    ticker,
    viewer,
    activeSection,
    flowSideBasis,
  }: {
    ticker: string;
    viewer: Viewer;
    activeSection: RightColumnList.Flow | RightColumnList.TickerGraphs;
    flowSideBasis: FlowSideBasisSelection;
  }) => {
    const { trades, isLoading, shouldHaveFree, shouldHavePaid, refetch } = useTickerFlowData(
      ticker,
      viewer,
      flowSideBasis,
    );

    if (activeSection === RightColumnList.Flow) {
      return (
        <TickerFlowSideTable
          ticker={ticker}
          trades={trades}
          isLoading={isLoading}
          shouldHaveFree={shouldHaveFree}
          shouldHavePaid={shouldHavePaid}
          onManualRefresh={refetch}
          flowSideBasis={flowSideBasis}
        />
      );
    }

    // TickerGraphs
    if (isLoading) {
      return (
        <div className="flex h-full w-full items-center justify-center p-4">
          <PulseLoader />
        </div>
      );
    }

    if (trades.length > 0) {
      return (
        <div className="h-[400px] w-full">
          <BarChartSection trades={trades} activeTicker={ticker} />
        </div>
      );
    }

    return (
      <div className="flex h-full w-full items-center justify-center p-4 text-gray-400">
        No options flow data available for {ticker}
      </div>
    );
  },
);

interface RightSidePanelProps {
  ticker: string;
  currentStockPrice: number;
  isExpanded?: boolean;
  onSetExpanded?: (
    expanded: boolean,
    currentModule: string,
    reason: 'user_toggle' | 'ineligible_widget',
  ) => void;
  className?: string;
}

// Memoize the component
export const RightSidePanel = React.memo(
  ({
    ticker,
    currentStockPrice,
    isExpanded = false,
    onSetExpanded,
    className,
  }: RightSidePanelProps) => {
    const viewer = useViewer();
    const [activeRightColumnSectionTop, setActiveRightColumnSectionTop] = useState(
      RightColumnList.Flow,
    );
    const flowSideBasis = useMemo(() => getDefaultFlowSideBasisSelection(), []);

    const isFlowSection =
      activeRightColumnSectionTop === RightColumnList.Flow ||
      activeRightColumnSectionTop === RightColumnList.TickerGraphs;

    const handleSelectWidget = (key: RightColumnList) => {
      setActiveRightColumnSectionTop(key);
      if (isExpanded && !isOpcExpandable(key)) {
        onSetExpanded?.(false, key, 'ineligible_widget');
      }
    };

    return (
      <div className={`flex h-full flex-col overflow-hidden ${className ?? ''}`}>
        <div className="mb-2 flex shrink-0 items-center gap-1">
          <div className="mr-1 flex items-center border-r border-palette-border pr-2">
            <LayoutToggleButton
              isExpanded={isExpanded}
              onClick={() =>
                onSetExpanded?.(!isExpanded, activeRightColumnSectionTop, 'user_toggle')
              }
              disabledReason={getOpcExpandDisabledReason(activeRightColumnSectionTop)}
            />
          </div>
          {rightColumnItems.map((item) => {
            return (
              <button
                key={item.key}
                className={`inline-flex items-center rounded-[4px] border px-2.5 py-2 text-sm font-medium ${
                  item.key === activeRightColumnSectionTop
                    ? 'border border-brand-primary bg-brand-primary'
                    : 'border-palette-black-6'
                }`}
                onClick={() => handleSelectWidget(item.key)}
                aria-label={RIGHT_COLUMN_LABELS[item.key]}
                title={RIGHT_COLUMN_LABELS[item.key]}
              >
                {item.name}
              </button>
            );
          })}
        </div>
        <div className="-p-4 min-h-0 w-full flex-1 overflow-auto bg-palette-black-2">
          {isFlowSection && (
            <FlowDataSection
              ticker={ticker}
              viewer={viewer}
              flowSideBasis={flowSideBasis}
              activeSection={
                activeRightColumnSectionTop as RightColumnList.Flow | RightColumnList.TickerGraphs
              }
            />
          )}
          {activeRightColumnSectionTop === RightColumnList.DarkPool && (
            <div className="h-full overflow-auto">
              <TickerDarkPoolTable activeTicker={ticker} viewer={viewer} activeTimeFilter={0} />
            </div>
          )}
          {activeRightColumnSectionTop === RightColumnList.Signals && (
            <SignalAlertTable
              viewer={viewer}
              ticker={ticker}
              asset={SignalAsset.Stock}
              watchListTickers={[ticker]}
              searchValue=""
            />
          )}
          {activeRightColumnSectionTop === RightColumnList.TopNews && (
            <TickerNews activeTicker={ticker} />
          )}

          {activeRightColumnSectionTop === RightColumnList.FundamentalAnalysis && (
            <FundamentalAnalysisWidget path={TICKER_FLOW_PAGE} activeTicker={ticker} />
          )}

          {activeRightColumnSectionTop === RightColumnList.TechnicalAnalysis && (
            <TechnicalAnalysisWidget path={TICKER_FLOW_PAGE} activeTicker={ticker} />
          )}
          {activeRightColumnSectionTop === RightColumnList.PriceTargetAnalysis && (
            <div className="mt-4 p-3">
              <SectionTitle
                Icon={TbReportSearch}
                title={`${ticker || 'Ticker'} Analyst Consensus`}
              />
              <PriceTarget ticker={ticker} currentStockPrice={currentStockPrice} />
              <BuyingRating ticker={ticker} />
            </div>
          )}
        </div>
      </div>
    );
  },
);
